import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Truck, FileDown, Repeat, XCircle, CreditCard } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["PENDING_PAYMENT", "CONFIRMED", "PROCESSING", "DISPATCHED", "DELIVERED", "CANCELLED"];

export function OrdersList() {
  const [tab, setTab] = useState("ALL");
  const [items, setItems] = useState([]);
  const load = async () => {
    const params = tab === "ALL" ? {} : { status: tab };
    const { data } = await api.get("/dealer/orders", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const repeat = async (o) => { try { await api.post(`/dealer/orders/${o.id}/repeat`); toast.success("Items added to cart"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const cancel = async (o) => { if (!window.confirm("Cancel this order?")) return; try { await api.post(`/dealer/orders/${o.id}/cancel`); toast.success("Cancelled"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="dealer-orders">
      <div><h1 className="font-display text-2xl sm:text-3xl font-black">My orders</h1><p className="text-muted-foreground">Live status, payment, tracking and POD.</p></div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="ALL" data-testid="dord-tab-ALL">All</TabsTrigger>
          {STATUSES.map((s) => <TabsTrigger key={s} value={s} data-testid={`dord-tab-${s}`}>{s.replace("_"," ")}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((o) => (
              <TableRow key={o.id} data-testid={`dord-row-${o.id}`}>
                <TableCell className="font-mono text-xs font-semibold"><Link to={`/dealer/orders/${o.id}`} className="text-primary hover:underline">{o.number}</Link></TableCell>
                <TableCell>{(o.lines || []).length}</TableCell>
                <TableCell>₹{(o.total || 0).toLocaleString()}</TableCell>
                <TableCell><Badge variant="outline" className={o.payment_status === "PAID" ? "bg-emerald-100 text-emerald-900" : ""}>{o.payment_status}</Badge></TableCell>
                <TableCell><Badge>{o.status.replace("_", " ")}</Badge></TableCell>
                <TableCell className="font-mono text-[10px]">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => repeat(o)} data-testid={`dord-repeat-${o.id}`} title="Repeat order"><Repeat className="w-4 h-4" /></Button>
                  {["PENDING_PAYMENT", "CONFIRMED"].includes(o.status) && <Button size="sm" variant="ghost" onClick={() => cancel(o)} data-testid={`dord-cancel-${o.id}`}><XCircle className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No orders yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function OrderDetail() {
  const { id } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [polling, setPolling] = useState(false);

  const load = async () => { const { data } = await api.get(`/dealer/orders/${id}`); setOrder(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Stripe return handling — poll status if ?session_id is present
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/dealer/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Payment successful — order confirmed!");
          setPolling(false);
          load();
          nav(`/dealer/orders/${id}`, { replace: true });
          return;
        }
        if (data.status === "expired") { toast.error("Payment session expired"); setPolling(false); return; }
      } catch { /* ignore */ }
      if (attempts < 8) setTimeout(tick, 2000);
      else setPolling(false);
    };
    tick();
    // eslint-disable-next-line
  }, [loc.search, id]);

  const payNow = async () => {
    try {
      const { data } = await api.post("/dealer/checkout/session", { order_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!order) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="dealer-order-detail">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/dealer/orders" className="text-xs text-muted-foreground hover:text-primary">← Back to orders</Link>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">{order.number}</h1>
          <div className="flex gap-2 mt-2"><Badge>{order.status.replace("_"," ")}</Badge><Badge variant="outline">{order.payment_status}</Badge></div>
        </div>
        <div className="flex gap-2">
          {order.payment_status !== "PAID" && order.status !== "CANCELLED" && <Button onClick={payNow} data-testid="order-pay"><CreditCard className="w-4 h-4 mr-1" /> Pay ₹{order.total.toLocaleString()}</Button>}
          <Button variant="outline" onClick={() => window.print()} data-testid="order-print"><FileDown className="w-4 h-4 mr-1" /> Invoice / Packing list</Button>
        </div>
      </div>
      {polling && <div className="surface-card p-3 text-sm text-amber-700 flex items-center gap-2"><Truck className="w-4 h-4" /> Confirming payment with Stripe…</div>}

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2"><CardContent className="p-5">
          <h2 className="font-display text-lg font-bold mb-3">Items</h2>
          <Table>
            <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Qty</TableHead><TableHead>Unit ₹</TableHead><TableHead className="text-right">Subtotal</TableHead></TableRow></TableHeader>
            <TableBody>
              {(order.lines || []).map((line, i) => (
                <TableRow key={i}><TableCell>{line.title}</TableCell><TableCell>{line.qty}</TableCell><TableCell>{line.unit_price}</TableCell><TableCell className="text-right font-semibold">₹{line.subtotal.toLocaleString()}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="text-right mt-3 space-y-1">
            <div className="text-sm flex justify-between"><span>Subtotal</span><span>₹{order.subtotal.toLocaleString()}</span></div>
            {order.loyalty_redeemed > 0 && <div className="text-sm flex justify-between text-violet-700"><span>Loyalty redeemed</span><span>− ₹{order.loyalty_redeemed.toLocaleString()}</span></div>}
            <div className="font-display text-xl font-black flex justify-between border-t pt-1"><span>Total</span><span>₹{order.total.toLocaleString()}</span></div>
            {order.earn_points > 0 && <div className="text-xs text-muted-foreground">+{order.earn_points} pts on payment</div>}
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-5">
          <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Tracking</h2>
          <div className="space-y-2">
            {(order.tracking || []).map((t, i) => (
              <div key={i} className="text-xs border-l-2 border-primary pl-3 py-1"><div className="font-semibold">{t.status}</div><div className="text-muted-foreground">{new Date(t.at).toLocaleString()} · {t.by}</div></div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground mt-3"><strong>Delivery:</strong> {order.delivery_address}</div>
          {order.vehicle_no && <div className="text-xs mt-1"><strong>Vehicle:</strong> {order.vehicle_no}</div>}
          {order.driver_name && <div className="text-xs"><strong>Driver:</strong> {order.driver_name} {order.driver_phone && `· ${order.driver_phone}`}</div>}
        </CardContent></Card>
      </div>
    </div>
  );
}
